import React, { useEffect, useContext, memo, useCallback } from 'react';
import { ThemeContext } from '../context/ThemeContext';
import videosData from '../data/javascripts/videosData';
import { Helmet } from 'react-helmet-async'; // Import Helmet

// Memoized Video Card component to prevent unnecessary re-renders
const VideoCard = memo(({ video, darkMode, onVideoClick }) => {
    const handleClick = useCallback(() => {
        onVideoClick(video.url);
    }, [video.url, onVideoClick]);

    const handleButtonClick = useCallback((e) => {
        e.stopPropagation();
        onVideoClick(video.url);
    }, [video.url, onVideoClick]);

    // Format view count
    const formatViews = (views) => {
        if (views >= 1000000) {
            return `${(views / 1000000).toFixed(1)}M views`;
        } else if (views >= 1000) {
            return `${(views / 1000).toFixed(1)}K views`;
        }
        return `${views} views`;
    };

    // Format duration
    const formatDuration = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Precomputed classes based on darkMode
    const cardClass = `card h-100 ${darkMode ? 'bg-dark border-secondary' : ''}`;
    const textClass = darkMode ? 'text-light' : 'text-muted';
    const titleClass = `card-title ${darkMode ? 'text-light' : 'text-dark'}`;
    const descriptionClass = `card-text ${darkMode ? 'text-light' : 'text-muted'} mb-3`;

    return (
        <div className="col mb-2">
            <div
                className={cardClass}
                onClick={handleClick}
                style={{ cursor: 'pointer' }}
            >
                <div className="position-relative" style={{ height: '200px', overflow: 'hidden' }}>
                    <img
                        src={
                            video.imageUrl
                                ? `/images/videos/${video.imageUrl}`
                                : '/images/default-video.webp'
                        } alt={video.title}
                        className="card-img-top h-100 w-100 object-fit-cover"
                        loading="lazy"
                        style={{
                            transition: 'transform 0.3s ease'
                        }}
                        onError={(e) => {
                            console.error("Error loading media element:", {
                                tag: e.target.tagName,
                                src: e.target.src
                            });
                            e.target.onerror = null;
                            e.target.src = "/images/default-video.webp";
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1.0)'}
                    />
                    {/* Duration badge */}
                    {video.duration && (
                        <span
                            className="badge bg-dark position-absolute"
                            style={{
                                bottom: '8px',
                                right: '8px',
                                fontSize: '0.75rem',
                                padding: '0.25rem 0.5rem',
                                opacity: 0.9
                            }}
                        >
                            {formatDuration(video.duration)}
                        </span>
                    )}
                    {/* Play icon overlay */}
                    <div
                        className="position-absolute top-50 start-50 translate-middle"
                        style={{
                            opacity: 0.8,
                            transition: 'opacity 0.3s ease',
                            pointerEvents: 'none'
                        }}
                    >
                        <i
                            className="bi bi-play-circle-fill text-white"
                            style={{ fontSize: '3.5rem' }}
                        ></i>
                    </div>
                </div>

                <div className="card-body d-flex flex-column">
                    <h5
                        className={titleClass}
                        style={{
                            fontSize: '1rem',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            minHeight: '3rem'
                        }}
                    >
                        {video.title}
                    </h5>
                    <p
                        className={descriptionClass}
                        style={{
                            fontSize: '0.875rem',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            flex: '1',
                            minHeight: '2.5rem'
                        }}
                    >
                        {video.description}
                    </p>
                    <div className="d-flex justify-content-between align-items-center mt-auto">
                        <small className={textClass}>
                            {video.views ? formatViews(video.views) : video.date}
                        </small>
                        <button
                            onClick={handleButtonClick}
                            className="btn btn-md btn-danger"
                        >
                            <i className="bi bi-play-fill me-1"></i>
                            Watch
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
});

// Main AllVideos component with featured filter prop
const AllVideos = () => {
    // Use window.scrollTo only on mount
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
    }, []);

    const { darkMode } = useContext(ThemeContext);

    // Memoize video click function
    const handleVideoClick = useCallback((url) => {
        window.open(url, '_blank', 'noopener,noreferrer');
    }, []);

    // Precomputed classes
    const sectionHeadingClass = `section-heading mb-2 ${darkMode ? 'text-light' : 'text-dark'}`;
    const containerClass = `mt-5 container mb-4`;

    return (
        <>
            {/* Add Helmet for SEO metadata */}
            <Helmet>
                <title>All Videos | YouTube & Media Gallery</title>
                <meta 
                    name="description" 
                    content="Browse the complete archive of technical, educational, and personal videos hosted on YouTube and other platforms." 
                />
                <meta 
                    name="keywords" 
                    content="all videos, YouTube gallery, technical videos, educational content, video archive, media" 
                />
            </Helmet>

            <div className={containerClass}>
                <div className="row mb-4">
                    <div className="mt-3 col-12 text-center mb-4">
                        <h2 className={sectionHeadingClass}>
                            <i className="bi bi-youtube text-danger me-2"></i>
                            {'All Videos'}
                        </h2>
                        {videosData.length === 0 && (
                            <p className={`lead ${darkMode ? 'text-light' : 'text-muted'}`}>
                                No featured videos available at the moment.
                            </p>
                        )}
                    </div>
                </div>

                <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
                    {videosData.map((video, index) => (
                        <VideoCard
                            key={video.id || video.url || index}
                            video={video}
                            darkMode={darkMode}
                            onVideoClick={handleVideoClick}
                        />
                    ))}
                </div>

                {/* Visit Channel Button */}
                <div className="row justify-content-center mt-5 g-2">
                    <div className="col-6 col-md-auto text-center mb-4">
                        <button
                            className="btn btn-lg btn-gradient mx-2"
                            onClick={() =>
                                window.open("https://www.youtube.com/@parallelroutines", "_blank")
                            }
                        >
                            Visit my channel
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default React.memo(AllVideos);